<script lang="ts" setup>
import isNil from 'lodash/isNil'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import BusyIndicator from '@/components/common/BusyIndicator.vue'
import CardContainer from '@/components/common/CardContainer.vue'
import NotificationBanner from '@/components/common/NotificationBanner.vue'
import PrintButton from '@/components/common/PrintButton.vue'
import LeagueRoundMatchesTable from '@/components/widgets/public/LeagueRoundMatchesTable.vue'
import LeagueRundownTable from '@/components/widgets/public/LeagueRundownTable.vue'
import LeagueStandingsTable from '@/components/widgets/public/LeagueStandingsTable.vue'
import { useLeaguesStore } from '@/store/public/leagues'
import { getErrorMessage } from '@/utils/error-handling'
import { formatDate } from '@/utils/format'

const leagues = useLeaguesStore()
const route = useRoute()

const errorMessage = ref<string | null>(null)
const isLoading = ref<boolean>(false)

const selectedRoundSectionNumber = computed<string>((): string => {
  if (isNil(leagues.selectedRound)) {
    return '?'
  }

  if (!isNil(leagues.selectedRound.section_number_extra)) {
    return `${leagues.selectedRound.section_number} / ${leagues.selectedRound.section_number_extra}`
  }

  return String(leagues.selectedRound.section_number)
})

onMounted(async () => {
  errorMessage.value = null
  isLoading.value = true

  try {
    await leagues.selectRoundById(route.params.round as string)
    await leagues.fetchLeagueRoundStandings()
  } catch (e) {
    const message = getErrorMessage(e as Error)
    console.error(`Failed fetching league standings: ${message}`)
    errorMessage.value = message
  }

  isLoading.value = false
})
</script>

<template>
  <h1 v-if="isLoading" class="busy-indicator-container">
    <BusyIndicator />
  </h1>

  <NotificationBanner
    v-else-if="errorMessage"
    :message="errorMessage"
    is-danger
  />

  <template v-else>
    <div class="table-controls">
      <PrintButton />
    </div>
    <CardContainer
      :title="
        $t('leagues.labels.section-round', {
          section: selectedRoundSectionNumber,
          separator: '»',
          round: leagues.selectedRound?.number ?? '?',
        })
      "
    >
      <NotificationBanner
        v-if="(leagues.selectedRound?.standings?.rows ?? []).length === 0"
        :message="$t('leagues.errors.no-round-results')"
        is-warning
      />

      <template v-else>
        <h4>
          {{ $t('leagues.labels.last-updated') }}:
          <span class="last-updated">
            {{
              formatDate(new Date(leagues.selectedRound?.standings?.timestamp))
            }}
          </span>
        </h4>

        <template
          v-if="(leagues.selectedRound?.standings?.matches ?? []).length > 0"
        >
          <h4>{{ $t('leagues.labels.matches') }}</h4>

          <LeagueRoundMatchesTable
            :rows="leagues.selectedRound?.standings?.matches"
            :round-id="leagues.selectedRound?.id"
          />
        </template>

        <h4>
          {{
            $t('leagues.labels.section', {
              section: leagues.selectedRound?.section_number,
            })
          }}
        </h4>

        <LeagueStandingsTable :rows="leagues.selectedRound?.standings?.rows" />

        <template
          v-if="(leagues.selectedRound?.standings?.rundown ?? []).length > 0"
        >
          <h4>{{ $t('leagues.labels.rundown') }}</h4>

          <LeagueRundownTable
            :rows="leagues.selectedRound?.standings?.rundown"
          />
        </template>

        <template v-if="leagues.selectedRound?.section_number_extra">
          <h4>
            {{
              $t('leagues.labels.section', {
                section: leagues.selectedRound?.section_number_extra,
              })
            }}
          </h4>

          <LeagueStandingsTable
            :rows="leagues.selectedRound?.standings?.rows_extra"
          />

          <template
            v-if="
              (leagues.selectedRound?.standings?.rundown_extra ?? []).length > 0
            "
          >
            <h4>{{ $t('leagues.labels.rundown') }}</h4>

            <LeagueRundownTable
              :rows="leagues.selectedRound?.standings?.rundown_extra"
            />
          </template>
        </template>
      </template>
    </CardContainer>
  </template>
</template>

<style lang="scss" scoped>
.busy-indicator-container {
  text-align: center;
}

.last-updated {
  font-weight: normal;
}
</style>
